import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import type {
  ReconciliationPhase,
  ReconciliationRow,
  ReconciliationSource,
  ReconciliationStatus
} from '@chessx/shared/types'

import {
  useMarkReconciled,
  useReconciliationList,
  useReconciliationSummary
} from './ReconciliationApi'
import './Reconciliation.scss'

const PAGE_SIZE = 25

const SOURCE_LABELS: Record<ReconciliationSource, string> = {
  'season-rollover': 'Season Rollover',
  continuation: 'Continuation'
}

const PHASE_OPTIONS: Array<{ value: ReconciliationPhase; source: ReconciliationSource }> = [
  { value: 'load', source: 'season-rollover' },
  { value: 'claim', source: 'season-rollover' },
  { value: 'credit', source: 'season-rollover' },
  { value: 'arm', source: 'continuation' },
  { value: 'resolve', source: 'continuation' },
  { value: 'stale', source: 'continuation' }
]

const STATUS_OPTIONS: ReconciliationStatus[] = ['unreconciled', 'reconciled', 'all']

const ageClass = (ageHours: number): string => {
  if (ageHours >= 24 * 7) return 'age-critical'
  if (ageHours >= 24) return 'age-warn'
  return 'age-fresh'
}

const formatAge = (ageHours: number): string => {
  if (ageHours < 24) return `${ageHours}h`
  return `${Math.floor(ageHours / 24)}d`
}

const formatDetails = (details: ReconciliationRow['details']): string =>
  Object.entries(details)
    .filter(([, value]) => value !== null && value !== '')
    .map(([key, value]) => `${key}: ${value}`)
    .join('  ·  ')

const Reconciliation = () => {
  const [source, setSource] = useState<ReconciliationSource | ''>('')
  const [phase, setPhase] = useState<ReconciliationPhase | ''>('')
  const [status, setStatus] = useState<ReconciliationStatus>('unreconciled')
  const [subjectInput, setSubjectInput] = useState('')
  const [subjectId, setSubjectId] = useState('')
  const [page, setPage] = useState(1)
  const [pendingRow, setPendingRow] = useState<ReconciliationRow | null>(null)
  const [note, setNote] = useState('')

  const summary = useReconciliationSummary()
  const list = useReconciliationList({
    source: source || undefined,
    phase: phase || undefined,
    status,
    subjectId: subjectId || undefined,
    page,
    limit: PAGE_SIZE
  })
  const markReconciled = useMarkReconciled()

  const rows = list.data?.rows ?? []
  const total = list.data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

  useEffect(() => {
    if (!list.isFetching && page > lastPage) setPage(lastPage)
  }, [lastPage, page, list.isFetching])

  const resetToFirstPage =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value)
      setPage(1)
    }

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubjectId(subjectInput.trim())
    setPage(1)
  }

  const confirmReconcile = async () => {
    if (!pendingRow) return
    await markReconciled
      .mutateAsync({
        source: pendingRow.source,
        id: pendingRow.id,
        note: note.trim() || undefined
      })
      .catch(() => undefined)
    setPendingRow(null)
    setNote('')
    list.refetch()
  }

  const phaseOptions = PHASE_OPTIONS.filter(option => !source || option.source === source)

  return (
    <div className="rec-page">
      <div className="rec-header">
        <div>
          <h3 className="rec-title">Reconciliation</h3>
          <span className="rec-subtitle">
            Dead-letter records whose payout was withheld and needs an operator
          </span>
        </div>
        <button className="rec-btn rec-btn-ghost" onClick={() => list.refetch()}>
          Refresh
        </button>
      </div>

      <div className="rec-summary">
        <div className="rec-stat rec-stat-total">
          <span className="rec-stat-value">{summary.data?.totalUnreconciled ?? '—'}</span>
          <span className="rec-stat-label">Unreconciled</span>
        </div>
        {summary.data?.bySource.map(entry => (
          <div key={entry.source} className="rec-stat">
            <span className="rec-stat-value">{entry.unreconciled}</span>
            <span className="rec-stat-label">{SOURCE_LABELS[entry.source]}</span>
            <span className="rec-stat-phases">
              {entry.byPhase
                .filter(item => item.count > 0)
                .map(item => `${item.phase} ${item.count}`)
                .join(' · ') || 'clear'}
            </span>
          </div>
        ))}
        <div className="rec-stat">
          <span className="rec-stat-value rec-stat-value-sm">
            {summary.data?.oldestUnreconciledAt
              ? new Date(summary.data.oldestUnreconciledAt).toLocaleDateString()
              : '—'}
          </span>
          <span className="rec-stat-label">Oldest outstanding</span>
        </div>
      </div>

      <form className="rec-filters" onSubmit={submitSearch}>
        <select
          className="rec-select"
          value={source}
          onChange={event => {
            resetToFirstPage(setSource)(event.target.value as ReconciliationSource | '')
            setPhase('')
          }}
        >
          <option value="">All sources</option>
          <option value="season-rollover">Season Rollover</option>
          <option value="continuation">Continuation</option>
        </select>

        <select
          className="rec-select"
          value={phase}
          onChange={event =>
            resetToFirstPage(setPhase)(event.target.value as ReconciliationPhase | '')
          }
        >
          <option value="">All phases</option>
          {phaseOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.value}
            </option>
          ))}
        </select>

        <select
          className="rec-select"
          value={status}
          onChange={event =>
            resetToFirstPage(setStatus)(event.target.value as ReconciliationStatus)
          }
        >
          {STATUS_OPTIONS.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <input
          className="rec-input"
          placeholder="User id or game id"
          value={subjectInput}
          onChange={event => setSubjectInput(event.target.value)}
        />
        <button className="rec-btn rec-btn-search" type="submit">
          Search
        </button>
      </form>

      <div className="rec-card">
        {list.isLoading ? (
          <div className="rec-loading">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="rec-skel-row">
                <div className="rec-skel-line w40" />
                <div className="rec-skel-line w25" />
              </div>
            ))}
          </div>
        ) : rows.length ? (
          <div className="rec-table-wrap">
            <table className="rec-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Phase</th>
                  <th>Subject</th>
                  <th>Age</th>
                  <th>Error</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={`${row.source}-${row.id}`}>
                    <td>
                      <span className={`rec-badge src-${row.source}`}>
                        {SOURCE_LABELS[row.source]}
                      </span>
                    </td>
                    <td>
                      <span className="rec-badge rec-badge-phase">{row.phase}</span>
                    </td>
                    <td className="col-subject">
                      <code>{row.subjectId}</code>
                      <span className="rec-subject-kind">{row.subjectKind}</span>
                      <span className="rec-details">{formatDetails(row.details)}</span>
                    </td>
                    <td>
                      <span className={`rec-age ${ageClass(row.ageHours)}`}>
                        {formatAge(row.ageHours)}
                      </span>
                    </td>
                    <td className="col-error" title={row.error}>
                      {row.error}
                    </td>
                    <td>
                      {row.reconciledAt ? (
                        <span
                          className="rec-badge rec-badge-done"
                          title={row.reconciliationNote ?? ''}
                        >
                          reconciled
                        </span>
                      ) : (
                        <span className="rec-badge rec-badge-open">open</span>
                      )}
                    </td>
                    <td className="col-actions">
                      {row.reconciledAt ? null : (
                        <button
                          className="rec-btn rec-btn-resolve"
                          onClick={() => {
                            setPendingRow(row)
                            setNote('')
                          }}
                        >
                          Mark reconciled
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rec-empty">
            <p>Nothing outstanding. Every withheld payout has been reconciled.</p>
          </div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="rec-pager">
          <button
            className="rec-btn rec-btn-ghost"
            disabled={page <= 1}
            onClick={() => setPage(current => current - 1)}
          >
            Previous
          </button>
          <span className="rec-pager-label">
            Page {page} of {lastPage} · {total} records
          </span>
          <button
            className="rec-btn rec-btn-ghost"
            disabled={page >= lastPage}
            onClick={() => setPage(current => current + 1)}
          >
            Next
          </button>
        </div>
      )}

      <Modal centered show={Boolean(pendingRow)} onHide={() => setPendingRow(null)}>
        <div className="rec-modal">
          <h4 className="rec-modal-title">Mark reconciled</h4>
          <p className="rec-modal-body">
            Confirm the owed payout for <code>{pendingRow?.subjectId}</code> has been settled by
            hand. This only closes the record — it pays nothing.
          </p>
          <textarea
            className="rec-textarea"
            placeholder="What did you do to settle it?"
            value={note}
            onChange={event => setNote(event.target.value)}
          />
          <div className="rec-modal-actions">
            <button className="rec-btn rec-btn-ghost" onClick={() => setPendingRow(null)}>
              Cancel
            </button>
            <button
              className="rec-btn rec-btn-resolve"
              disabled={markReconciled.isPending}
              onClick={confirmReconcile}
            >
              {markReconciled.isPending ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Reconciliation
